/**
 * useReservationFilterOptions — offer/client/salesperson filter-pill option
 * lists for the /reservations route.
 *
 * Extracted from ReservationsPage (mirrors the existing useReservationActions /
 * useReservationPermissions hook-extraction pattern already used by this
 * feature) so the route component stays within the max-lines-per-function
 * budget. Neutral home (CU-86cbek5j5 extraction) since it reads only from
 * features/offers and features/clients, never features/reservations itself —
 * cross-feature import of those from features/reservations is forbidden
 * (WEB_ARCHITECTURE.md:36).
 *
 * Visibility model: the offer list scope mirrors reservation-list visibility
 * (force_release holders see every offer, everyone else sees only their own —
 * their reservation list is already server-scoped to their own holdings, so
 * this mirrors that). The client/salesperson hooks (CU-86cb7ctfw) fail closed
 * to an empty list when the caller lacks the underlying permission, degrading
 * to an empty pill rather than throwing.
 */
import * as React from 'react';
import { useOfferSalesUsers } from '../features/offers/api';
import { useClientsList } from '../features/clients/api';
import { normalizeClientList, useOffersList } from '@ooh/screens';

export interface ReservationFilterOption {
  value: string;
  label: string;
}

export interface UseReservationFilterOptionsResult {
  offerFilterOptions: ReservationFilterOption[];
  clientFilterOptions: ReservationFilterOption[];
  salespersonFilterOptions: ReservationFilterOption[];
}

export function useReservationFilterOptions(
  canForceRelease: boolean,
): UseReservationFilterOptionsResult {
  // useOffersList (CU-86cb4wjwk) drains every paginated page automatically, so
  // this dropdown keeps seeing every offer the caller can filter by.
  const { data: offerFilterOptionsData } = useOffersList(canForceRelease ? 'all' : 'mine');
  const offerFilterOptions = React.useMemo(
    () => (offerFilterOptionsData ?? []).map((o) => ({ value: String(o.id), label: o.title })),
    [offerFilterOptionsData],
  );

  const { data: clientFilterOptionsData } = useClientsList();
  const clientFilterOptions = React.useMemo(
    () =>
      normalizeClientList(clientFilterOptionsData).map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    [clientFilterOptionsData],
  );

  const { data: salespersonFilterOptionsData } = useOfferSalesUsers();
  const salespersonFilterOptions = React.useMemo(
    () => (salespersonFilterOptionsData ?? []).map((u) => ({ value: String(u.id), label: u.name })),
    [salespersonFilterOptionsData],
  );

  return { offerFilterOptions, clientFilterOptions, salespersonFilterOptions };
}
